import type { ReactElement } from 'react';

/**
 * Simple 2px line icons (spec: Visual system → Icons). Always decorative: every primary control
 * also carries a visible text label, so icons are hidden from assistive technology.
 */

const PATHS = {
  discover: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5z" />
    </>
  ),
  watch: (
    <>
      <rect x="3" y="5" width="18" height="13" rx="2" />
      <path d="M10 9l5 2.5-5 2.5z" />
      <path d="M8 21h8" />
    </>
  ),
  play: (
    <>
      <path d="M7 8h10a4 4 0 0 1 4 4v1.5a2.5 2.5 0 0 1-4.6 1.4L15 13H9l-1.4 1.9A2.5 2.5 0 0 1 3 13.5V12a4 4 0 0 1 4-4z" />
      <path d="M7.5 10v2.5M6.25 11.25h2.5" />
      <path d="M15.5 11h.01M17 12.5h.01" />
    </>
  ),
  create: (
    <>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="M12 12l8-4.5M12 12v9M12 12L4 7.5" />
    </>
  ),
  projects: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  skills: (
    <>
      <path d="M5 21V4" />
      <path d="M5 4h11l-2 4 2 4H5" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.7-2.5 2-2.5 4" />
      <path d="M12 17h.01" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  'chevron-left': <path d="M15 5l-7 7 7 7" />,
  'chevron-right': <path d="M9 5l7 7-7 7" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l5 5" />
    </>
  ),
  captions: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 11h4M13 11h4M7 15h7M16 15h1" />
    </>
  ),
  audio: (
    <>
      <path d="M4 10v4h4l5 4V6L8 10z" />
      <path d="M16.5 9a4 4 0 0 1 0 6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  alert: (
    <>
      <path d="M12 3.5l9.5 16.5h-19z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5h.01" />
    </>
  ),
  circuit: <path d="M13 3L5 13.5h6L10 21l8-10.5h-6z" />,
  broadcaster: (
    <>
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path d="M8 3l4 4 4-4" />
    </>
  ),
  language: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 3.5 5.7 3.5 9s-1 6.3-3.5 9c-2.5-2.7-3.5-5.7-3.5-9s1-6.3 3.5-9z" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
    </>
  ),
  'media-play': <path d="M8 5.5v13l10.5-6.5z" />,
  'media-pause': <path d="M8.5 5.5v13M15.5 5.5v13" />,
  'back-10': (
    <>
      <path d="M4 12a8 8 0 1 0 2.4-5.7L4 8.7" />
      <path d="M4 4v4.7h4.7" />
      <path d="M10 10v5M13 10h2v5h-2z" />
    </>
  ),
  'forward-10': (
    <>
      <path d="M20 12a8 8 0 1 1-2.4-5.7L20 8.7" />
      <path d="M20 4v4.7h-4.7" />
      <path d="M9 10v5M12 10h2v5h-2z" />
    </>
  ),
  'volume-off': (
    <>
      <path d="M4 10v4h4l5 4V6L8 10z" />
      <path d="M16.5 9.5l5 5M21.5 9.5l-5 5" />
    </>
  ),
  fullscreen: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  'fullscreen-exit': <path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5" />,
} satisfies Record<string, ReactElement>;

export type IconName = keyof typeof PATHS;
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  return (
    <svg
      className="ui-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
